import React from 'react'
import { FenceElement, FenceSystem } from '../contexts/FenceContext'
import { getFenceConfig, getFenceConfigWithColor } from '../config/fenceConfigs'

interface QuotePreviewProps {
  elements: FenceElement[]
  selectedSystem: FenceSystem | null
  selectedColor: string | null
  totalLength: number
  totalPrice: number
  className?: string
}

export const QuotePreview: React.FC<QuotePreviewProps> = ({
  elements,
  selectedSystem,
  selectedColor,
  totalLength,
  totalPrice,
  className = ''
}) => {
  const currentDate = new Date().toLocaleDateString('pl-PL')
  const fenceConfig = selectedSystem ? getFenceConfig(selectedSystem.id) : null
  
  console.log(`🔍 QuotePreview RENDER:`, {
    selectedSystem,
    dimensions: selectedSystem?.dimensions,
    elementsCount: elements.length
  })
  
  const getPostBaseLength = (element?: FenceElement) => {
    // 🎯 Dla muro mix i royal mix: użyj szerokości z wymiarów bloczka
    const isMuroMix = selectedSystem?.id?.startsWith('lupane-muro')
    const isRoyalMix = selectedSystem?.id?.startsWith('royal') || selectedSystem?.id === 'royal-mix'
    
    // Dla muro mix: sprawdź wariant i użyj szerokości z wymiarów bloczka
    if (isMuroMix && element?.variant) {
      // P22: 38×22×16 → szerokość = 38cm = 0.38m
      // P38: 38×38×16 → szerokość = 38cm = 0.38m
      // S22: (12+26)×22×16 → szerokość = 38cm = 0.38m (12+26=38)
      // S38: (12+26)×38×16 → szerokość = 38cm = 0.38m (12+26=38)
      // Wszystkie warianty Muro Mix mają szerokość słupka 38cm
      if (element.variant === 'p22' || element.variant === 'p38' || element.variant === 's22' || element.variant === 's38') {
        return 0.38 // 38cm
      }
    }
    
    // Dla pojedynczych configów Muro (bez wariantu) - wszystkie mają 38cm
    if (selectedSystem?.id === 'lupane-muro-p22' || selectedSystem?.id === 'lupane-muro-p38' || 
        selectedSystem?.id === 'lupane-muro-s22' || selectedSystem?.id === 'lupane-muro-s38') {
      return 0.38 // 38cm
    }
    
    // Dla royal mix (S50/P50): sprawdź wariant i wymiary
    if (isRoyalMix && element?.variant) {
      // Royal S50: 50x40x20 → szerokość = 50cm = 0.5m
      // Royal P50: 50x20x20 → szerokość = 50cm = 0.5m
      if (element.variant === 's50' || element.variant === 'p50') {
        return 0.5 // 50cm
      }
    }
    
    // Dla innych wariantów: parsuj wymiary z konfiguracji
    if (selectedSystem?.dimensions && !selectedSystem.dimensions.includes('Wieloformatowe')) {
      // Parsuj wymiary (może być "50x40x20" lub "38×38×16")
      const dimensions = selectedSystem.dimensions.split(/[×x]/)
      const blockWidthCm = parseInt(dimensions[0]) || 100
      return blockWidthCm / 100
    }
    
    // Fallback: użyj spacing z konfiguracji
    if (fenceConfig?.elements?.post?.spacing) {
      return fenceConfig.elements.post.spacing * 2
    }
    return 0.4
  }

  const getElementLength = (element: FenceElement) => {
    if (element.type === 'post') {
      return getPostBaseLength(element)
    }
    if (element.type === 'section') {
      // 🎯 Dla sekcji: oblicz szerokość jako szerokość bloczka × multiplier (dla wyświetlania użytkownikowi)
      
      // 🎯 Dla muro mix i royal mix: sprawdź wariant i użyj szerokości bloczka z wymiarów
      const isMuroMix = selectedSystem?.id?.startsWith('lupane-muro')
      const isRoyalMix = selectedSystem?.id?.startsWith('royal') || selectedSystem?.id === 'royal-mix'
      const variant = element.variant || (selectedSystem?.id === 'lupane-muro-p38' ? 'p38' : 
                                          selectedSystem?.id === 'lupane-muro-p22' ? 'p22' :
                                          selectedSystem?.id === 'lupane-muro-s38' ? 's38' :
                                          selectedSystem?.id === 'lupane-muro-s22' ? 's22' :
                                          selectedSystem?.id === 'royal_s50' ? 's50' :
                                          selectedSystem?.id === 'royal_p50' ? 'p50' : undefined)
      
      let blockWidth: number // Szerokość pojedynczego bloczka w metrach
      
      if (isMuroMix && variant) {
        // Dla muro mix: użyj szerokości bloczka z wymiarów
        // P38: 38×38×16 → szerokość bloczka = 38cm = 0.38m
        // P22: 38×22×16 → szerokość bloczka = 38cm = 0.38m
        // S38: (12+26)×38×16 → szerokość bloczka = 38cm = 0.38m
        // S22: 22×22×16 → szerokość bloczka = 22cm = 0.22m
        if (variant === 's22') {
          blockWidth = 0.22 // 22cm
        } else {
          blockWidth = 0.38 // 38cm (dla p38, p22, s38)
        }
      } else if (isRoyalMix && variant) {
        // Dla royal mix: użyj szerokości bloczka z wymiarów
        // Royal S50: 50x40x20 → szerokość bloczka = 50cm = 0.5m
        // Royal P50: 50x20x20 → szerokość bloczka = 50cm = 0.5m
        if (variant === 's50' || variant === 'p50') {
          blockWidth = 0.5 // 50cm
        } else {
          // Fallback dla innych wariantów royal
          blockWidth = 0.5
        }
      } else if (selectedSystem?.dimensions && !selectedSystem.dimensions.includes('Wieloformatowe')) {
        // Dla innych ogrodzeń: parsuj wymiary (pierwsza wartość to szerokość bloczka)
        // Obsługuje zarówno "×" jak i "x"
        const dimensions = selectedSystem.dimensions.split(/[×x]/)
        const blockWidthCm = parseInt(dimensions[0]) || 100
        blockWidth = blockWidthCm / 100
      } else if (fenceConfig?.elements?.section?.spacing) {
        // Fallback: użyj spacing * 2 (dla starszych konfiguracji)
        blockWidth = fenceConfig.elements.section.spacing * 2
      } else if (fenceConfig?.elements?.section?.defaultWidth) {
        blockWidth = fenceConfig.elements.section.defaultWidth
      } else {
        blockWidth = 0.396 // Fallback
      }
      
      const widthMultiplier = element.widthMultiplier || 1
      const calculatedWidth = blockWidth * widthMultiplier
      
      // Użyj obliczonej szerokości zamiast element.width (który może być nieaktualny)
      return calculatedWidth
    }
    return element.width || 0
  }
  
  // 🎯 Załaduj konfiguracje wariantów dla wszystkich elementów
  const [variantConfigs, setVariantConfigs] = React.useState<Record<string, any>>({})
  
  React.useEffect(() => {
    const loadVariantConfigs = async () => {
      const configs: Record<string, any> = {}
      
      for (const element of elements) {
        if (element.variant && fenceConfig?.variantConfigs?.[element.variant]) {
          const variantConfigId = fenceConfig.variantConfigs[element.variant]
          const key = `${element.id}-${element.variant}`
          if (!configs[key]) {
            const variantConfig = await getFenceConfigWithColor(variantConfigId, selectedColor || undefined)
            if (variantConfig) {
              configs[key] = variantConfig
            }
          }
        }
      }
      
      setVariantConfigs(configs)
    }
    
    if (elements.length > 0 && fenceConfig?.variantConfigs) {
      loadVariantConfigs()
    }
  }, [elements, fenceConfig, selectedColor])
  
  const getElementHeight = (element: FenceElement) => {
    // 🎯 Dla wariantów (Royal Mix / Muro Mix): Użyj konfiguracji wariantu
    let currentFenceConfig = fenceConfig
    
    if (element.variant && fenceConfig?.variantConfigs?.[element.variant]) {
      const key = `${element.id}-${element.variant}`
      const variantConfig = variantConfigs[key]
      if (variantConfig) {
        currentFenceConfig = variantConfig
      }
    }
    
    // 🎯 Pobierz blockHeight z konfiguracji (wariantu lub głównej)
    // Najpierw sprawdź czy są wymiary w selectedSystem.dimensions
    let blockHeightCm = 20 // domyślna
    
    if (currentFenceConfig?.blockHeight) {
      blockHeightCm = currentFenceConfig.blockHeight
    } else if (selectedSystem?.blockHeight) {
      blockHeightCm = selectedSystem.blockHeight
    } else if (selectedSystem?.dimensions && !selectedSystem.dimensions.includes('Wieloformatowe')) {
      // Parsuj wymiary (może być "50x40x20" lub "38×38×16")
      const dimensions = selectedSystem.dimensions.split(/[×x]/)
      if (dimensions.length >= 3) {
        blockHeightCm = parseInt(dimensions[2]) || 20
      }
    }
    
    const blockHeightM = blockHeightCm / 100
    
    // 🎯 Pobierz decorationBlockHeight z konfiguracji (dla post lub section)
    const decorationBlockHeightCm =
      currentFenceConfig?.elements?.[element.type as 'post' | 'section']?.decorationBlockHeight ??
      currentFenceConfig?.elements?.post?.decorationBlockHeight ??
      currentFenceConfig?.blockHeight ??
      blockHeightCm
    const decorationBlockHeightM = decorationBlockHeightCm / 100
    
    // 🎯 Pobierz liczbę bloków (z element.height lub defaultHeight)
    const totalBlocks = (() => {
      if (typeof element.height === 'number' && element.height > 0) {
        return element.height
      }
      const defaultHeight =
        currentFenceConfig?.elements?.[element.type as 'post' | 'section']?.defaultHeight ??
        currentFenceConfig?.elements?.post?.defaultHeight ??
        1
      return defaultHeight
    })()
    
    // 🎯 Pobierz decorationBlocksCount z konfiguracji
    const decorationBlocksCount =
      currentFenceConfig?.elements?.[element.type as 'post' | 'section']?.decorationBlocksCount ??
      currentFenceConfig?.elements?.post?.decorationBlocksCount ??
      1
    
    if (element.type === 'post' || element.type === 'section') {
      const decorative = Math.min(decorationBlocksCount, totalBlocks)
      const regular = Math.max(totalBlocks - decorative, 0)
      const regularHeight = regular * blockHeightM
      const decorationHeight = decorative * decorationBlockHeightM
      return regularHeight + decorationHeight
    }
    
    return element.height || 0
  }
  
  // 🎯 Pobierz rzeczywistą rotację z 3D sceny
  const getRealRotation = (elementId: string, fallbackRotation: number) => {
    const element3D = (window as any).elements3DRef?.current?.get(elementId)
    if (element3D && element3D.userData) {
      // Priorytet: inheritedRotation > reactRotation > rotation > element.rotation
      return element3D.userData.inheritedRotation !== undefined ? element3D.userData.inheritedRotation :
             element3D.userData.reactRotation !== undefined ? element3D.userData.reactRotation :
             element3D.userData.rotation !== undefined ? element3D.userData.rotation :
             fallbackRotation || 0
    }
    return fallbackRotation || 0
  }
  
  const getElementTypeName = (type: string, elementIndex: number, element?: FenceElement) => {
    const typeNames = {
      'post': 'Słupek',
      'section': 'Przęsło',
      'gate': 'Brama',
      'door': 'Furtka'
    }
    
    const baseName = typeNames[type as keyof typeof typeNames] || type || 'Nieznany'
    
    // Dodaj numerację dla słupków - liczymy tylko słupki
    if (type === 'post') {
      const postNumber = elements.slice(0, elementIndex + 1).filter(el => el.type === 'post').length
      return `${baseName} ${postNumber}`
    }
    
    // 🎯 Rozpoznaj sekcję zamykającą obwód po fladze isClosingSection lub ID zaczynającym się od "closing-"
    if (type === 'section' && (element?.isClosingSection || (element?.id && element.id.startsWith('closing-')))) {
      return 'Przęsło zamykające'
    }
    
    return baseName
  }
  
  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
      <div className="mb-6">
        <h3 className="text-lg font-bold text-gray-900 mb-2">Podgląd oferty</h3>
        <p className="text-sm text-gray-500">Data: {currentDate}</p>
      </div>
      
      
      {/* Szczegóły ogrodzenia */}
      <div className="mb-6">
        <h4 className="font-semibold text-gray-900 mb-2">Szczegóły ogrodzenia</h4>
        <div className="text-sm text-gray-700 space-y-1">
          {selectedSystem && <p><strong>System:</strong> {selectedSystem.name}</p>}
          {selectedColor && <p><strong>Kolor:</strong> {selectedColor}</p>}
          <p><strong>Całkowita długość:</strong> {elements.reduce((sum, element) => sum + getElementLength(element), 0).toFixed(2)} m</p>
          <p><strong>Liczba elementów:</strong> {elements.length}</p>
        </div>
      </div>
      
      {/* Lista elementów */}
      <div className="mb-6">
        <h4 className="font-semibold text-gray-900 mb-2">Lista elementów</h4>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-3 py-2 text-left font-medium text-gray-700">Typ</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">Wymiary</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">Cena</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {elements.map((element, index) => {
                // 🚪 Dla bram i drzwi: pokazuj tylko szerokość (bez wysokości)
                const isGateOrDoor = element.type === 'gate' || element.type === 'door';
                if (isGateOrDoor) {
                  console.log(`[QuotePreview] Gate/Door detected: type="${element.type}", showing only width`);
                }
                const dimensionsText = isGateOrDoor
                  ? `${getElementLength(element).toFixed(2)}m`
                  : `${getElementLength(element).toFixed(2)}m × ${getElementHeight(element).toFixed(2)}m`;
                
                return (
                  <tr key={element.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-3 py-2 text-gray-900">{getElementTypeName(element.type, index, element)}</td>
                    <td className="px-3 py-2 text-gray-700">
                      {dimensionsText}
                    </td>
                    <td className="px-3 py-2 text-gray-900 font-medium">
                      {element.type !== 'door' && element.type !== 'gate' ? `${(element.price || 0).toFixed(2)} zł` : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Podsumowanie finansowe */}
      <div className="border-t pt-4">
        <h4 className="font-semibold text-gray-900 mb-2">Podsumowanie finansowe</h4>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-700">Wartość netto:</span>
              <span className="text-gray-900">{(totalPrice / 1.23).toFixed(2)} zł</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-700">VAT (23%):</span>
              <span className="text-gray-900">{(totalPrice - totalPrice / 1.23).toFixed(2)} zł</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="font-semibold text-gray-900">Wartość brutto:</span>
              <span className="font-bold text-blue-600 text-lg">{totalPrice.toFixed(2)} zł</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default QuotePreview





